import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatchTasks } from "../../../../hooks/useDispatchTasks";
import { RoleIds, TaskEnums } from "../../../../common/constants";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import { useEntities } from "@emberly/zenith-client";
import { DRIVERS_AND_OPERATORS_QUERY } from "../../../../common/queries";
import { getLocalStorageValue, setLocalStorageValue } from "../../../../common/helpers";
import fuzzysort from "fuzzysort";


export const DispatchContext = createContext();

export const useDispatch = () => useContext(DispatchContext);

export const DispatchProvider = (props) => {
  const { t } = useTranslation();

  // Fetch initial filter values from localstorage
  const defaultFilters = useMemo(() => {
    const data = JSON.parse(getLocalStorageValue("dispatch_filters", "{}"))
    return {
      urgencyFilter: data?.urgencyFilter || null,
      planningFilter: data?.planningFilter || -1,
      lowPriorityFilter: typeof data?.lowPriorityFilter === "boolean" ? data?.lowPriorityFilter : true,
      normalPriorityFilter: typeof data?.normalPriorityFilter === "boolean" ? data?.normalPriorityFilter : true,
      highPriorityFilter: typeof data?.highPriorityFilter === "boolean" ? data?.highPriorityFilter : true,
    }
  }, []);

  // Filters
  const [urgencyFilter, setUrgencyFilter] = useState(defaultFilters.urgencyFilter);
  const [planningFilter, setPlanningFilter] = useState(defaultFilters.planningFilter);
  const [lowPriorityFilter, setLowPriorityFilter] = useState(defaultFilters.lowPriorityFilter);
  const [normalPriorityFilter, setNormalPriorityFilter] = useState(defaultFilters.normalPriorityFilter);
  const [highPriorityFilter, setHighPriorityFilter] = useState(defaultFilters.highPriorityFilter);
  const [searchFilter, setSearchFilter] = useState("");

  // Sort
  const [orderTasksBy, setOrderTasksBy] = useState(getLocalStorageValue("orderTasksByV2", "urgency"));
  

  // Filter enabled
  const filterEnabled = urgencyFilter !== null || planningFilter !== -1 || lowPriorityFilter !== true || normalPriorityFilter !== true || highPriorityFilter !== true;

  // Reset filter and wipe the localstorage
  const resetFilter = useCallback(() => {
    setUrgencyFilter(null);
    setPlanningFilter(-1);
    setLowPriorityFilter(true);
    setNormalPriorityFilter(true);
    setHighPriorityFilter(true);
    setLocalStorageValue("dispatch_filters", "{}");
    setSearchFilter("");
  }, []);

  // Deffered saving of filters to local storage
  useEffect(() => {
    const timer = setTimeout(
      () => setLocalStorageValue("dispatch_filters", JSON.stringify({
        urgencyFilter,
        planningFilter,
        lowPriorityFilter,
        normalPriorityFilter,
        highPriorityFilter,
      })), 500
    );

    return () => clearTimeout(timer);
  }, [
    urgencyFilter,
    planningFilter,
    lowPriorityFilter,
    normalPriorityFilter,
    highPriorityFilter,
  ]);

  // Fetch task and employee entities
  const { entities: employees } = useEntities("Employee", DRIVERS_AND_OPERATORS_QUERY);
  const { activeTasks, completedTasks, initialLoad, loading, entities, completedTasks24H } = useDispatchTasks(employees);
  const showLoading = !initialLoad || loading && entities.length === 0;

  // Apply filters to employees
  const { drivers, operators, activeDrivers, activeOperators, unassignedDrivers } = useMemo(() => {
    const drivers = employees.filter(e => e?.roles?.find(r => r.id === RoleIds.Driver));
    const operators = employees.filter(e => e?.active && e?.roles?.find(r => r.id === RoleIds.Operator));
    const activeDrivers = drivers.filter(e => e?.active);
    const activeOperators = operators.filter(e => e?.active);

    // make set of assigned driver ids
    let assignedDriverIds = new Set(activeTasks?.map(task => task?.execution?.driver?.id).filter(id => !!id));

    // find drivers with no tasks asssigned
    const unassignedDrivers = activeDrivers.filter(drv => !assignedDriverIds.has(drv.id))

    return {
      drivers,
      operators,
      activeDrivers,
      activeOperators,
      unassignedDrivers,
    }
  }, [employees, activeTasks]);


  // Define planning filters and fetch urgency count map
  const { urgencyCounts, planningFilters } = useMemo(() => {

    const now = moment();
    const endOfDay = moment().endOf("day");
    const endOfWeek = moment().endOf("week");

    const in2hours = moment().add(2, "hour");
    const in5hours = moment().add(5, "hour");
    const in24hours = moment().add(24, "hour");

    const isExpired = (task, compareDate) => {
      return !task.plannedEndMoment || task.plannedEndMoment.isSameOrBefore(compareDate);
    }

    // TODO next session!!
    const planningFilters = [
      {
        label: t("common:filters:planning:none"), // All tasks that are not planned, and those with a expired plannedEnd
        filter: (task) => task.planningType === TaskEnums.PlanningType.None || isExpired(task, now),
      },
      {
        label: t("common:filters:planning:next2hours"), // needs to be completed within the next 2 hours
        filter: (task) => isExpired(task, in2hours),
      },
      {
        label: t("common:filters:planning:next5hours"), // needs to be completed within the next 5 hours
        filter: (task) => isExpired(task, in5hours),
      },
      {
        label: t("common:filters:planning:today"), // is plannedEnd today
        filter: (task) => isExpired(task, endOfDay),
      },
      {
        label: t("common:filters:planning:next24hours"), // needs to be completed within the next 24 hours
        filter: (task) => isExpired(task, in24hours),
      },
      {
        label: t("common:filters:planning:thisWeek"), // needs to be completed this week
        filter: (task) => isExpired(task, endOfWeek),
      },
    ];

    return {
      planningFilters,
      urgencyCounts: {
        [TaskEnums.EstimatedUrgency.None]: activeTasks.reduce((acc, task) => acc + (task.urgency === TaskEnums.EstimatedUrgency.None ? 1 : 0), 0),
        [TaskEnums.EstimatedUrgency.Low]: activeTasks.reduce((acc, task) => acc + (task.urgency === TaskEnums.EstimatedUrgency.Low ? 1 : 0), 0),
        [TaskEnums.EstimatedUrgency.Normal]: activeTasks.reduce((acc, task) => acc + (task.urgency === TaskEnums.EstimatedUrgency.Normal ? 1 : 0), 0),
        [TaskEnums.EstimatedUrgency.High]: activeTasks.reduce((acc, task) => acc + (task.urgency === TaskEnums.EstimatedUrgency.High ? 1 : 0), 0),
        [TaskEnums.EstimatedUrgency.Critical]: activeTasks.reduce((acc, task) => acc + (task.urgency === TaskEnums.EstimatedUrgency.Critical ? 1 : 0), 0),
      }
    }
  }, [activeTasks])

  // Apply filters to tasks
  const filteredActiveTasks = useMemo(() => {

    const activePlanningFilter = planningFilter !== -1 ? planningFilters[planningFilter] : null;

    // apply search filter
    const res = searchFilter !== "" ?
      fuzzysort.go(
        searchFilter,
        activeTasks,
        {
          keys: [
            "execution.driver.name",
            "missionDetails.description",
            "missionTarget.carDetails.make",
            "missionTarget.boatDetails.make",
            "missionTarget.carDetails.model",
            "missionTarget.boatDetails.model",
            "missionTarget.carDetails.registration",
            "missionTarget.boatDetails.registration",
            (obj) => obj.missionDetails?.tags?.map(t => t.name).join()
          ]
        }
      ).filter(t => t.score > 0.3).map(t => t.obj) : activeTasks;

    return res.filter(t => (
      ( // Urgency filter
        urgencyFilter === null || urgencyFilter === t.urgency
      )
      &&
      ( // Planning filter
        activePlanningFilter === null || activePlanningFilter.filter(t)
      )
      &&
      ( // Priority filters
        (lowPriorityFilter === true && t.priority === TaskEnums.Priority.Low) ||
        (normalPriorityFilter === true && t.priority === TaskEnums.Priority.Normal) ||
        (highPriorityFilter === true && t.priority === TaskEnums.Priority.High)
      )

    ));
  }, [activeTasks, urgencyFilter, planningFilter, lowPriorityFilter, normalPriorityFilter, highPriorityFilter, planningFilters, searchFilter]);


  return (
    <DispatchContext.Provider
      value={{
        activeTasks,
        completedTasks,
        completedTasks24H,
        showLoading,
        urgencyFilter,
        setUrgencyFilter,
        planningFilter,
        setPlanningFilter,
        lowPriorityFilter,
        setLowPriorityFilter,
        normalPriorityFilter,
        setNormalPriorityFilter,
        highPriorityFilter,
        setHighPriorityFilter,
        searchFilter,
        setSearchFilter,
        filteredActiveTasks,
        planningFilters,
        urgencyCounts,
        activeDrivers,
        activeOperators,
        drivers,
        operators,
        unassignedDrivers,
        filterEnabled,
        resetFilter,
        orderTasksBy, 
        setOrderTasksBy,
        initialLoad
      }}
    >
      {props.children}
    </DispatchContext.Provider>
  );
};


